import { Button } from '@app/components/various/Button';
import { compiledPaths } from '@app/paths';
import { useIsAtBottom } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { Order } from '@typings/@centra';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { CheckoutButton } from '../CheckoutButton';
import { CheckoutPayments } from '../CheckoutPayments';
import { CheckoutSubtotal } from '../CheckoutSubtotal';
import { CheckoutSubtotalSkeleton } from '../CheckoutSubtotal/CheckoutSubtotalSkeleton';

interface Props {
  orderDetails: Order.Single;
  isUpdatingShippingCost: boolean;
  isPrepaymentEnabled: boolean;
  isSavingOrLoading: boolean;
  isSubmittingOrder: boolean;
  hasUnitsAdded: boolean;
  isRequestingPayment: boolean;
  handleEditSelection: () => void;
}
export const SelectionSummary = ({
  isUpdatingShippingCost,
  orderDetails,
  isPrepaymentEnabled,
  isSavingOrLoading,
  isSubmittingOrder,
  hasUnitsAdded,
  isRequestingPayment,
  handleEditSelection,
}: Props) => {
  const { t } = useTranslation(['selections', 'payments']);
  const ref = React.useRef<HTMLDivElement>(null);
  const isAtBottom = useIsAtBottom(ref);

  const amountToPay = orderDetails.order.totals.prepaymentPriceAsText;

  return (
    <div
      ref={ref}
      className="scrollbar-always top-[calc(var(--headerHeight)+var(--breadcrumbsHeight)+1rem)] z-topBar flex max-h-[calc(100vh-var(--headerHeight)-var(--breadcrumbsHeight)-2rem)] flex-col gap-4 self-start rounded-se-regular rounded-ss-regular bg-white px-8 lg:sticky lg:overflow-y-auto lg:rounded-regular"
    >
      <div className="py-6">
        {isUpdatingShippingCost ?
          <CheckoutSubtotalSkeleton />
        : <CheckoutSubtotal />}
      </div>
      {isPrepaymentEnabled && <CheckoutPayments order={orderDetails.order} />}
      <div
        className={cn(
          'sticky -bottom-14 -left-8 right-8 -mx-8 hidden w-[calc(100%+4rem)] flex-col items-center gap-2 rounded-ee-regular rounded-es-regular bg-white px-8 py-4 lg:bottom-0 lg:flex',
          {
            'border-t border-neutral-90': !isAtBottom,
          },
        )}
      >
        {isDefined(amountToPay) && (
          <div className="flex w-full justify-between pb-2">
            <span className="text-neutral-40 body-1">{t('payments:payment_amount_title')}:</span>
            <span className="body-1">{amountToPay}</span>
          </div>
        )}
        <CheckoutButton
          orderDetails={orderDetails}
          isDisabled={isSavingOrLoading || isUpdatingShippingCost || !hasUnitsAdded}
          isLoading={isSubmittingOrder || isRequestingPayment}
          className="md:max-w-screen-xxs"
        />
        <Button
          as={Link}
          to={compiledPaths.SELECTION_DETAILS({ id: orderDetails.order.order })}
          isFluid
          onClick={handleEditSelection}
          variant="bordered"
          color="secondary"
          type="button"
          dataTestId="selectionStep"
          className="md:max-w-screen-xxs"
        >
          {t('selections:edit_selection')}
        </Button>
      </div>
    </div>
  );
};
